import React, {
	useCallback,
	useState,
} from 'react';
import {
	Row,
	Col,
	Typography,
	notification,
	Space,
	Image,
	Empty,
	Upload,
	Checkbox,
	Dropdown,
	MenuProps,
	Tag,
	Tooltip,
} from 'antd';
import {
	MailOutlined,
	EyeOutlined,
	DeleteOutlined,
	ReloadOutlined,
} from '@ant-design/icons';
import {
	useParams,
	useHistory,
} from 'react-router-dom';
import { ColumnsType } from 'antd/lib/table/interface';
import sortBy from 'lodash.sortby';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	faEllipsisStrokeVertical,
	faEnvelope,
	faLink,
	faFacePensive,
	faKey,
	faRefresh,
	faTrash,
	faUserCrown,
} from '@fortawesome/pro-light-svg-icons';
import { standardSort } from '@shared/utils/standardSort';
import { getLoginMethod, LoginMethods } from '@shared/utils/getLoginMethod';
import type { UserProps } from '@api/models/user';
import type { VesselProps } from '@api/models/vessel';
import type { UpdateOrganizationRequest } from '@api/features/admin/updateOrganization';
import Table from '@client/components/Table/Table';
import {
	getOrganizationDetails,
	updateOrganization,
	deleteOrganization as apiDeleteOrganization,
	sendAdminInvitation,
	updateOrganizationLogo,
	sendWelcomeEmail,
	updateUser,
	toggleUserDisabled,
	updateOrganizationUser,
	sendUserInvitation,
	getUserInvitationLink,
	setupUserPasswordLogin,
} from '@client/lib/api';
import FormPopover from '@client/components/FormPopover';
import showSuccessNotification from '@client/utils/showSuccessNotification';
import showErrorNotification from '@client/utils/showErrorNotification';
import { useImpersonation } from '@client/lib/impersonation';
import useFetchedState from '@client/utils/hooks/useFetchedState';
import SimpleScreen from '@client/components/screens/SimpleScreen';
import EditableDetails from '@client/components/EditableDetails/EditableDetails';
import Button from '@client/components/Button';
import { Links } from '@client/utils/links';
import { Field } from '@client/components/SimpleForm';
import Card from '@client/components/Card/Card';
import styles from './OrganizationDetailsScreen.module.css';
import getItems from './helpers/getItems';
import InviteUsersDrawer from './components/InviteUsersDrawer';

type FileType = {
	lastModified: number;
	name: string;
	size: number;
	type: string;
	uid: string;
	webkitRelativePath: string;
};

const getActions = (
	user: UserProps,
	onDisableUser: (userId: number) => void,
	toggleManagement: (userId: number, isManagement: boolean) => void,
	resendInvitation: (userId: number) => void,
	copyInvitationLink: (userId: number) => void,
	setupPasswordLogin: (userId: number) => void,
) => {
	const loginMethod = getLoginMethod(user.providerId);
	const hasPasswordLogin = loginMethod === LoginMethods.PASSWORD;

	const items: MenuProps['items'] = [
		{
			label: (
				<Space>
					<FontAwesomeIcon icon={user.isManagement ? faFacePensive : faUserCrown} />
					{user.isManagement ? 'Remove from management' : 'Promote to management'}
				</Space>
			),
			key: 'management',
			onClick: () => toggleManagement(user.id, user.isManagement),
		},
		{
			label: (
				<Space>
					<FontAwesomeIcon icon={faEnvelope} />
					Send invitation
				</Space>
			),
			onClick: () => resendInvitation(user.id),
			key: 'invite',
			disabled: !hasPasswordLogin,
		},
		{
			label: (
				<Space>
					<FontAwesomeIcon icon={faLink} />
					Copy invitation link
				</Space>
			),
			onClick: () => copyInvitationLink(user.id),
			key: 'copyInviteLink',
			disabled: !hasPasswordLogin,
		},
		...(loginMethod === LoginMethods.MISSING ? [{
			label: (
				<Space>
					<FontAwesomeIcon icon={faKey} />
					Set up password login
				</Space>
			),
			onClick: () => setupPasswordLogin(user.id),
			key: 'setupPasswordLogin',
		}] : []),
		{
			type: 'divider',
		},
		{
			label: (
				<Space>
					<FontAwesomeIcon icon={user.disabled ? faRefresh : faTrash} />
					{user.disabled ? 'Re-activate user' : 'Disable user'}
				</Space>
			),
			style: { color: 'red' },
			key: '0',
			onClick: () => onDisableUser(user.id),
		},
	];

	return items;
};

const getUserColumns = (
	onSendWelcomeEmail: (userId: number) => void,
	setShouldReceiveEmails: (value: boolean, userId: number) => void,
	toggleDisabled: (userId: number) => void,
	toggleManagement: (userId: number, isManagement: boolean) => void,
	resendInvitation: (userId: number) => void,
	copyInvitationLink: (userId: number) => void,
	setupPasswordLogin: (userId: number) => void,
): ColumnsType<UserProps> =>
	[
		{
			title: 'Id',
			dataIndex: 'id',
			key: 'id',
			width: 40,
			align: 'center',
		},
		{
			title: 'Name',
			dataIndex: 'name',
			key: 'name',
			render: (name, row) => {
				const isDeleted = row.deletedAt != null;

				return (
					<Space size={4}>
						{row.isManagement && (
							<Tooltip title="Management">
								<FontAwesomeIcon icon={faUserCrown} />
							</Tooltip>
						)}
						{(row.disabled || isDeleted) ? (
							<Tooltip title={isDeleted ? 'Deleted' : 'Disabled'}>
								<span style={{ textDecoration: 'line-through' }}>{name}</span>
							</Tooltip>
						) : name}
					</Space>
				);
			},
		},
		{
			title: 'Email',
			dataIndex: 'email',
			key: 'email',
		},
		{
			title: 'Login',
			dataIndex: 'providerId',
			key: 'providerId',
			render: (providerId: string | null) => {
				switch (getLoginMethod(providerId)) {
					case LoginMethods.SSO:
						return (<Tag color="blue">SSO</Tag>);
					case LoginMethods.PASSWORD:
						return (<Tag>Password</Tag>);
					default:
						return (
							<Tooltip title='This user has no login and cannot sign in. Use "Set up password login" in the actions menu.'>
								<Tag color="red">Missing</Tag>
							</Tooltip>
						);
				}
			},
		},
		{
			title: 'Welcome email',
			dataIndex: 'hasReceivedWelcomeEmail',
			key: 'hasReceivedWelcomeEmail',
			render: (hasReceivedWelcomeEmail, row) => (
				<Button
					icon={hasReceivedWelcomeEmail ? (<ReloadOutlined />) : (<MailOutlined />)}
					onClick={() => onSendWelcomeEmail(row.id)}
					type={hasReceivedWelcomeEmail ? 'default' : 'primary'}
					disabled={!row.shouldReceiveEmails || row.deletedAt != null}
					disabledTooltip={row.deletedAt != null ?
						'User is deleted' :
						'Enable "Should receive emails" to send a welcome email'}
				>
					{hasReceivedWelcomeEmail ? 'Re-send' : 'Send welcome email'}
				</Button>
			),
		},
		{
			title: 'Should receive emails?',
			dataIndex: 'shouldReceiveEmails',
			key: 'shouldReceiveEmails',
			render: (shouldReceiveEmails, row) => (
				<Checkbox
					checked={shouldReceiveEmails}
					disabled={row.deletedAt != null}
					onChange={(event) => {
						setShouldReceiveEmails(event.target.checked, row.id);
					}}
				/>
			),
		},
		{
			title: 'Actions',
			align: 'right',
			render: (_, row) => (
				<Space>
					<Dropdown
						disabled={row.deletedAt != null}
						menu={{
							items: getActions(
								row,
								toggleDisabled,
								toggleManagement,
								resendInvitation,
								copyInvitationLink,
								setupPasswordLogin,
							),
						}}
						trigger={['click']}
					>
						<Button icon={(<FontAwesomeIcon icon={faEllipsisStrokeVertical} />)} />
					</Dropdown>
				</Space>
			),
		},
	];

type AdminInvitationValues = {
	name: string;
	email: string;
	external: boolean;
	isManagement: boolean;
};

const adminInvitationFields: Field<AdminInvitationValues>[] = [
	{
		label: 'First name',
		name: 'name',
		type: 'text',
		required: true,
	},
	{
		label: 'Email',
		name: 'email',
		type: 'text',
		required: true,
	},
	{
		label: 'Non-office 365 user?',
		name: 'external',
		type: 'switch',
		required: true,
	},
	{
		label: 'Is management',
		name: 'isManagement',
		type: 'switch',
		required: true,
	},
];

const OrganizationDetailsScreen: React.FC = () => {
	const params = useParams<{ id: string }>();
	const id = Number(params.id);

	const history = useHistory();
	const { beginImpersonating } = useImpersonation();

	const [inviteUsersOpen, setInviteUsersOpen] = useState(false);

	const vesselColumns: ColumnsType<VesselProps> = [
		{
			title: 'Id',
			dataIndex: 'id',
			key: 'id',
			width: 40,
			align: 'center',
			sortOrder: 'ascend',
			sorter: standardSort('id'),
		},
		{
			title: 'Name',
			dataIndex: 'name',
			key: 'name',
		},
	];

	const [
		organization,
		refreshOrganization,
		error,
		loading,
	] = useFetchedState(
		() => getOrganizationDetails(id),
		[],
		{ showNotification: false },
	);

	const {
		name,
		users,
		vessels,
	} = (organization ?? {
		name: '',
		users: [],
		vessels: [],
	});

	const toggleDisabled = async (userId: number) => {
		if (organization == null) {
			return;
		}

		try {
			await toggleUserDisabled(organization.id, userId);
			showSuccessNotification('Success');
			await refreshOrganization();
		} catch (_e) {
			showErrorNotification('Could not update user');
		}
	};

	const onSendWelcomeEmail = async (userId: number) => {
		if (organization != null) {
			await sendWelcomeEmail(organization.id, userId);
			await refreshOrganization();
		}
	};

	const setShouldReceiveEmails = async (value: boolean, userId: number) => {
		await updateUser({
			userId,
			attributes: {
				shouldReceiveEmails: value,
			},
		});
		refreshOrganization();
	};

	const toggleManagement = async (userId: number, isManagement: boolean) => {
		if (organization == null) {
			return;
		}

		try {
			await updateOrganizationUser(
				userId,
				organization.id,
				{
					isManagement: !isManagement,
				},
			);
			showSuccessNotification('Success');
			await refreshOrganization();
		} catch (e) {
			showErrorNotification('Could not update user', e as Error);
		}
	};

	const resendInvitation = async (userId: number) => {
		if (organization == null) {
			return;
		}

		try {
			await sendUserInvitation(
				userId,
				organization.id,
			);
			showSuccessNotification('Success');
			await refreshOrganization();
		} catch (e) {
			showErrorNotification('Could not update user', e as Error);
		}
	};

	const copyInvitationLink = async (userId: number) => {
		if (organization == null) {
			return;
		}

		try {
			const { link } = await getUserInvitationLink(organization.id, userId);
			await navigator.clipboard.writeText(link);
			showSuccessNotification('Invitation link copied', 'The link is valid for 7 days');
		} catch (e) {
			showErrorNotification('Could not get invitation link', e as Error);
		}
	};

	const setupPasswordLogin = async (userId: number) => {
		if (organization == null) {
			return;
		}

		try {
			await setupUserPasswordLogin(organization.id, userId);
			showSuccessNotification('Password login set up', 'You can now send or copy an invitation link');
			await refreshOrganization();
		} catch (e) {
			showErrorNotification('Could not set up password login', e as Error);
		}
	};

	const userColumns = getUserColumns(
		onSendWelcomeEmail,
		setShouldReceiveEmails,
		toggleDisabled,
		toggleManagement,
		resendInvitation,
		copyInvitationLink,
		setupPasswordLogin,
	);

	const saveDetails = useCallback(async (values: Partial<UpdateOrganizationRequest['attributes']>) => {
		await updateOrganization(id, {
			...values,
			...(values.useVesselCompanyOnInvoice != null ? {
				useVesselCompanyOnInvoice: values.useVesselCompanyOnInvoice,
			} : {}),
		});
		await refreshOrganization();

		if (values.featureFlags != null) {
			// Timeout so React has time to re-render and disable nav block
			setTimeout(() => {
				window.location.reload();
			}, 100);
		}
	}, [id, refreshOrganization]);

	const onUploadImage = async (file: FileType) => {
		try {
			await updateOrganizationLogo(id, file);
			await refreshOrganization();
		} catch (e) {
			showErrorNotification('Could not upload image', e as Error);
		}
	};

	const deleteOrganization = useCallback(async () => {
		try {
			await apiDeleteOrganization(id);

			notification.success({
				message: 'Organization was deleted',
				description: `${name} has successfully been deleted.`,
			});

			history.goBack();
		} catch (e) {
			showErrorNotification('Could not delete organization', e as Error);
		}
	}, [id, history, name]);

	const onSubmitInvite = (values: AdminInvitationValues) => {
		try {
			sendAdminInvitation(id, values.name, values.email, values.external, values.isManagement);
			showSuccessNotification(
				'Invitation sent',
				`An invitation was successfully sent to ${values.email}`,
			);
		} catch (e) {
			showErrorNotification('Could send invitation', e as Error);
		}
	};

	const detailItems = organization == null ? [] : getItems(organization);

	return (
		<SimpleScreen
			canGoBack
			error={error}
			breadcrumbs={[['Organizations', Links.Organizations.get()]]}
			rootPageTitle="Organizations"
			title={(
				<Typography.Text
					className={styles.name}
				>
					{name}
				</Typography.Text>
			)}
			loading={name == null}
			headerActions={[
				users.length === 0 ? (
					<FormPopover
						key="invite-admin"
						onSubmit={onSubmitInvite}
						buttonText="Invite Admin"
						title="Send admin invitation email"
						fields={adminInvitationFields}
						disabled={users.length > 0}
						buttonProps={{
							icon: (<MailOutlined />),
						}}
					/>
				) : (
					<Button
						type="primary"
						onClick={() => setInviteUsersOpen(true)}
						icon={(<MailOutlined />)}
					>
						Invite Team Members
					</Button>
				),
				(
					<Button
						confirmTitle={`Are you sure you want to delete ${name}?`}
						onClick={deleteOrganization}
						key="delete"
						type="primary"
						danger
					>
						Delete organization
					</Button>
				),
			]}
			className={undefined}
		>
			<Row gutter={16}>
				<Col span={8}>
					<Card
						title="Details"
					>
						{detailItems != null && (
							<EditableDetails
								items={detailItems}
								onSave={saveDetails}
							/>
						)}
					</Card>
				</Col>
				<Col span={16}>
					<Row gutter={[16, 16]}>
						<Col span={24}>
							<Card
								slim
								title={(
									<>
										Team Members
										{' '}
										<Button
											type="link"
											icon={(<EyeOutlined />)}
											onClick={() => beginImpersonating(id)}
										/>
									</>
								)}
							>
								<Table
									size="small"
									columns={userColumns}
									rowKey="id"
									dataSource={sortBy(users, [
										// Management first, then alphabetically
										(u) => !u.isManagement,
										(u) => (u.name ?? '').toLowerCase(),
									])}
									loading={loading}
								/>
							</Card>
						</Col>
						<Col span={24}>
							<Card
								slim
								title="Vessels"
							>
								<Table
									size="small"
									columns={vesselColumns}
									rowKey="id"
									dataSource={vessels}
									loading={loading}
								/>
							</Card>
						</Col>
						<Col span={24}>
							<Card
								slim
								bordered
								title="Organization logo"
								extra={(
									<Space>
										<Button
											type="link"
											danger
											icon={(<DeleteOutlined />)}
											onClick={() => saveDetails({ logoObjectKey: null })}
										/>
										<Upload
											beforeUpload={(file) => onUploadImage(file)}
											showUploadList={false}
										>
											<Button type="primary">Upload new</Button>
										</Upload>
									</Space>
								)}
							>
								<Space
									direction="vertical"
									size="middle"
									className={styles.logoWrapper}
								>
									<div className={styles.centerContent}>
										{organization?.logoUrl == null ? (

											<Empty description="No logo provided" />

										) : (
											<Image
												width={200}
												src={organization?.logoUrl}
											/>
										)}
									</div>
								</Space>
							</Card>
						</Col>
					</Row>
				</Col>
			</Row>
			<InviteUsersDrawer
				open={inviteUsersOpen}
				onClose={() => setInviteUsersOpen(false)}
				organizationDetails={organization ?? {}}
			/>
		</SimpleScreen>
	);
};

export default OrganizationDetailsScreen;
